import { useEffect, useState } from "react"
import { useWorkspaceEntitySplitDesktop } from "@/components/workspace/workspace-entity-split-layout"

export const WORKSPACE_ENTITY_CONTENT_ATTR = "data-workspace-entity-content"

const ZERO = { left: "0px", right: "0px" } as const

export type WorkspaceEntityContentInset = {
  left: string
  right: string
}

/**
 * CSS left/right insets so a fixed panel matches the entity-split content column
 * (leaves the agent column and anything past content.right uncovered).
 * Measures synchronously from ResizeObserver — no rAF — so the panel can snap without lag.
 */
export function useWorkspaceEntityContentInset(enabled: boolean): WorkspaceEntityContentInset {
  const isDesktop = useWorkspaceEntitySplitDesktop()
  const [inset, setInset] = useState<WorkspaceEntityContentInset>(ZERO)

  useEffect(() => {
    if (!enabled || !isDesktop) {
      setInset(ZERO)
      return
    }

    let observer: ResizeObserver | null = null

    const measure = () => {
      const nodes = document.querySelectorAll<HTMLElement>(`[${WORKSPACE_ENTITY_CONTENT_ATTR}]`)
      // Shot panel is portaled after the page split. Its preview is the editor target.
      const el = nodes.length > 0 ? nodes[nodes.length - 1] : null
      if (!el) {
        setInset(ZERO)
        return
      }
      const rect = el.getBoundingClientRect()
      const left = Math.max(0, Math.round(rect.left))
      // +1px so the fullscreen panel stops left of the agent split border (z under panel).
      const right = Math.max(0, Math.round(window.innerWidth - rect.right) + 1)
      setInset((prev) => {
        const nextLeft = `${left}px`
        const nextRight = `${right}px`
        if (prev.left === nextLeft && prev.right === nextRight) return prev
        return { left: nextLeft, right: nextRight }
      })
    }

    const bind = () => {
      observer?.disconnect()
      const nodes = document.querySelectorAll<HTMLElement>(`[${WORKSPACE_ENTITY_CONTENT_ATTR}]`)
      const el = nodes.length > 0 ? nodes[nodes.length - 1] : null
      if (!el) {
        setInset(ZERO)
        return
      }
      observer = new ResizeObserver(measure)
      observer.observe(el)
      if (el.parentElement) observer.observe(el.parentElement)
      measure()
    }

    bind()
    window.addEventListener("resize", measure)

    return () => {
      observer?.disconnect()
      window.removeEventListener("resize", measure)
    }
  }, [enabled, isDesktop])

  return enabled && isDesktop ? inset : ZERO
}
